import { buildProjectExport, prepareProjectImport, type ProjectExportFile } from '@/lib/projectTransfer';
import { SUPPORT_LIMITS, supportViewSchema, type SupportView } from '@/lib/schemas/support.schema';
import type { EditorProbe } from './editorProbe';

// ── La copie du projet jointe a un probleme (R4, R5, R7) ──
// Le fichier .archifacil du projet affiche, compresse en gzip. La vue y est placee DANS le projet (supportView) :
// remapProjectIds renumerote toute cle …Id, si bien qu'a l'ouverture l'etage et la selection suivent, sans table de
// correspondance (E05).

export type Snapshot = { blob: Blob; rawBytes: number; takenAt: string } | { tooLarge: true; rawBytes: number };

/** La vue a restituer, tiree de l'etat de l'editeur */
export function viewOf(probe: EditorProbe): SupportView {
  return {
    viewMode: probe.viewMode ?? '2d',
    ...(probe.activeFloorId ? { activeFloorId: probe.activeFloorId } : {}),
    ...(probe.selection ? { selectedElementId: probe.selection.id, selectedElementType: probe.selection.type } : {}),
    ...(probe.camera && probe.viewMode === '3d' ? { camera: probe.camera } : {}),
  };
}

/** Le poids du projet en JSON, mesure une fois par etat du projet (l'objet change a chaque modification) */
const weights = new WeakMap<object, number>();
export function projectBytes(project: EditorProbe['project']): number {
  let bytes = weights.get(project);
  if (bytes === undefined) {
    bytes = new TextEncoder().encode(JSON.stringify(project)).length;
    weights.set(project, bytes);
  }
  return bytes;
}

const gzip = (text: string) =>
  new Response(new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'))).blob();

export async function buildSnapshot(probe: EditorProbe): Promise<Snapshot> {
  const takenAt = new Date().toISOString();
  const text = JSON.stringify(buildProjectExport({ ...probe.project, supportView: viewOf(probe) } as EditorProbe['project']));
  const rawBytes = new TextEncoder().encode(text).length;
  if (rawBytes > SUPPORT_LIMITS.snapshotMaxBytes) return { tooLarge: true, rawBytes };
  const compressed = await gzip(text);
  return { blob: new Blob([compressed], { type: 'application/gzip' }), rawBytes, takenAt };
}

/** Le JSON d'une copie, decompresse */
export async function readSnapshot(gz: Blob): Promise<string> {
  return new Response(gz.stream().pipeThrough(new DecompressionStream('gzip'))).text();
}

/** Le nom d'une copie de support : « [Support n° 42] Maison Dubois » (R14) */
export const supportCopyName = (ticketNumber: number, name: string) => `[Support n° ${ticketNumber}] ${name || 'Sans titre'}`;

/**
 * Ouvre une copie (E05) : decompresse, renumerote par l'import existant (d'ou l'erreur lisible d'un format trop recent),
 * nomme le projet d'apres la demande, et rend la vue, ses identifiants deja renumerotes, otee du projet.
 */
export async function openSnapshot(gz: Blob, ticketNumber: number): Promise<{ project: EditorProbe['project']; view: SupportView }> {
  const text = await readSnapshot(gz);
  const imported = prepareProjectImport(text) as EditorProbe['project'] & { supportView?: unknown };
  const original = (JSON.parse(text) as Partial<ProjectExportFile>).project?.name ?? '';
  const { supportView, ...project } = imported;
  const view = supportViewSchema.safeParse(supportView);
  return { project: { ...project, name: supportCopyName(ticketNumber, original) }, view: view.success ? view.data : { viewMode: '2d' } };
}

/** Le fichier .archifacil d'une copie, tel qu'on l'importe ailleurs : sans la vue du support */
export async function snapshotExportText(gz: Blob): Promise<{ text: string; name: string }> {
  const file = JSON.parse(await readSnapshot(gz)) as ProjectExportFile & { project: { supportView?: unknown } };
  if (file.project) delete file.project.supportView;
  return { text: JSON.stringify(file), name: file.project?.name ?? 'projet' };
}
